import React from 'react';
import { Card, Typography, Collapse, Space, Divider, Button } from 'antd';
import { FileTextOutlined, DownloadOutlined, UserOutlined, BookOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const ManualUsuario = () => {
    return (
        <div className="p-6">
            <Card className="shadow-md">
                <div className="flex justify-between items-center mb-6">
                    <Title level={2} className="mb-0">
                        <BookOutlined className="mr-2" />
                        Manual de Usuario - SNAD
                    </Title>
                    <Button 
                        type="primary" 
                        icon={<DownloadOutlined />} 
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        Descargar PDF
                    </Button>
                </div>
                
                <Paragraph className="text-lg mb-6">
                    Bienvenido al manual de usuario del Software de Novedades Administrativas y Docentes (SNAD). 
                    Esta guía le proporcionará toda la información necesaria para utilizar el sistema de manera efectiva.
                </Paragraph>
                
                <Divider />
                
                <Collapse defaultActiveKey={['1']} className="mb-6">
                    <Panel header={<Text strong>1. Introducción al sistema</Text>} key="1">
                        <Paragraph>
                            El Software de Novedades Administrativas y Docentes (SNAD) es una herramienta diseñada para gestionar 
                            las novedades del personal administrativo y docente de la Universidad de Investigación y Desarrollo (UDI).
                        </Paragraph>
                        <Paragraph>
                            A través de esta plataforma podrá:
                            <ul>
                                <li>Registrar novedades de personal como: permisos, compensatorios, incapacidades, entre otros.</li>
                                <li>Consultar el histórico de novedades.</li>
                                <li>Gestionar usuarios del sistema (solo administradores).</li>
                                <li>Generar reportes y exportar datos.</li>
                            </ul>
                        </Paragraph>
                    </Panel>
                    
                    <Panel header={<Text strong>2. Acceso al sistema</Text>} key="2">
                        <Space direction="vertical" size="middle" className="w-full">
                            <Paragraph>
                                Para ingresar al sistema debe utilizar sus credenciales institucionales:
                                <ul>
                                    <li><strong>Email:</strong> Su correo electrónico institucional (@udi.edu.co)</li>
                                    <li><strong>Contraseña:</strong> Su contraseña personal</li>
                                    <li><strong>Rol:</strong> Seleccionar el rol correspondiente (Administrador o Profesional de Dependencia)</li>
                                </ul>
                            </Paragraph>
                            <Paragraph>
                                Si no dispone de credenciales o ha olvidado su contraseña, contacte al departamento de Tecnología.
                            </Paragraph>
                        </Space>
                    </Panel>
                    
                    <Panel header={<Text strong>3. Módulo de Novedades</Text>} key="3">
                        <Space direction="vertical" size="middle" className="w-full">
                            <div>
                                <Title level={5}>3.1 Consulta de Novedades</Title>
                                <Paragraph>
                                    En la pantalla principal del módulo de novedades encontrará una tabla con todas las novedades registradas.
                                    Puede utilizar los filtros disponibles para buscar por:
                                    <ul>
                                        <li>Campus</li>
                                        <li>Fecha</li>
                                        <li>Tipo de Novedad</li>
                                        <li>Texto en cualquier campo</li>
                                    </ul>
                                </Paragraph>
                            </div>
                            
                            <div>
                                <Title level={5}>3.2 Registro de Novedades</Title>
                                <Paragraph>
                                    Para registrar una nueva novedad:
                                    <ol>
                                        <li>Haga clic en el botón "Agregar Novedad"</li>
                                        <li>Complete todos los campos del formulario</li>
                                        <li>Seleccione si la novedad afecta o no a la nómina</li>
                                        <li>Añada cualquier observación relevante</li>
                                        <li>Guarde los cambios</li>
                                    </ol>
                                </Paragraph>
                            </div>
                            
                            <div>
                                <Title level={5}>3.3 Exportación de Datos</Title>
                                <Paragraph>
                                    Puede exportar los datos de novedades en formato Excel utilizando el botón "Exportar"
                                    ubicado en la esquina superior derecha de la tabla.
                                </Paragraph>
                            </div>
                        </Space>
                    </Panel>
                    
                    <Panel header={<Text strong>4. Administración de Usuarios</Text>} key="4">
                        <Space direction="vertical" size="middle" className="w-full">
                            <Paragraph>
                                Este módulo está disponible exclusivamente para usuarios con rol de Administrador.
                                Permite gestionar los usuarios que tienen acceso al sistema.
                            </Paragraph>
                            
                            <div>
                                <Title level={5}>4.1 Creación de Usuarios</Title>
                                <Paragraph>
                                    Para crear un nuevo usuario:
                                    <ol>
                                        <li>Haga clic en el botón "Nuevo Usuario"</li>
                                        <li>Complete la información personal del usuario</li>
                                        <li>Asigne un correo institucional y contraseña temporal</li>
                                        <li>Seleccione el rol adecuado</li>
                                        <li>Establezca el estado como "Activo"</li>
                                        <li>Guarde los cambios</li>
                                    </ol>
                                </Paragraph>
                            </div>
                            
                            <div>
                                <Title level={5}>4.2 Gestión de Usuarios</Title>
                                <Paragraph>
                                    Desde la tabla de usuarios puede:
                                    <ul>
                                        <li>Editar información de usuarios existentes</li>
                                        <li>Cambiar contraseñas</li>
                                        <li>Desactivar usuarios (cambiando su estado a "Inactivo")</li>
                                        <li>Eliminar usuarios (acción irreversible)</li>
                                    </ul>
                                </Paragraph>
                            </div>
                        </Space>
                    </Panel>
                    
                    <Panel header={<Text strong>5. Soporte Técnico</Text>} key="5">
                        <Paragraph>
                            Si experimenta problemas técnicos o tiene dudas sobre el funcionamiento del sistema,
                            puede contactar al equipo de soporte a través de:
                            <ul>
                                <li><strong>Email:</strong> soporte@udi.edu.co</li>
                                <li><strong>Extensión:</strong> 123</li>
                                <li><strong>Horario de atención:</strong> Lunes a Viernes de 8:00 AM a 5:00 PM</li>
                            </ul>
                        </Paragraph>
                    </Panel>
                </Collapse>
                
                <Divider />
                
                <div className="text-center text-gray-500">
                    <Paragraph>
                        © {new Date().getFullYear()} Universidad de Investigación y Desarrollo - UDI<br />
                        Versión 1.0 - Software de Novedades Administrativas y Docentes
                    </Paragraph>
                </div>
            </Card>
        </div>
    );
};

export default ManualUsuario;